/* JC-EVO 09/25/2026: videos design C.
   Shared fullscreen video player for the product Videos tab (sections/product-videos.liquid) and the custom-video film
   block (blocks/custom-video.liquid). Both load this file as a module script: deferred, and it runs once per page even
   when both include it.

   Triggers (event delegation, so hidden tabs and late markup work):
     [data-jc-lightbox="youtube"][data-video-id]       -> youtube-nocookie player, created only now (autoplay)
     [data-jc-lightbox="template"][data-template="id"] -> clones <template id="id"> (Shopify <video>, or an iframe)
     data-video-title = plain-text name for the dialog and the iframe title
   Dialog: native <dialog> + showModal() (top layer, rest of the page inert). Close button (focused on open, always above
   the player), backdrop click and Esc close it. Esc cannot reach the page while focus is inside the cross-origin YouTube
   iframe, so the close button stays visible outside the player. Closing removes the player (stops playback), releases
   the theme's scroll lock and returns focus to the poster. */
import { lockScroll, unlockScroll } from '@theme/utilities';

let dlg, stage, closeBtn, trigger, active = false;

const X = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5 5 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

function build() {
  dlg = document.createElement('dialog');
  dlg.className = 'jcv-lb';
  dlg.innerHTML = `<button type="button" class="jcv-lb__close" aria-label="Close video">${X}</button><div class="jcv-lb__stage"></div>`;
  stage = dlg.querySelector('.jcv-lb__stage');
  closeBtn = dlg.querySelector('.jcv-lb__close');
  closeBtn.addEventListener('click', close);
  dlg.addEventListener('click', (e) => { if (e.target === dlg) close(); });     // the black area around the player
  dlg.addEventListener('cancel', (e) => { e.preventDefault(); close(); });      // Esc
  dlg.addEventListener('close', cleanup);                                       // any other way the dialog closes
  document.body.appendChild(dlg);
}

function cleanup() {
  if (!active) return;
  active = false;
  const v = stage.querySelector('video');
  if (v) v.pause();
  stage.replaceChildren();                                                      // removing the player stops playback
  unlockScroll(dlg);
  const t = trigger; trigger = null;
  if (t && t.isConnected) t.focus({ preventScroll: true });
}

function close() {
  if (dlg && dlg.open) dlg.close();                                             // fires 'close' -> cleanup()
  else cleanup();
}

function open(media, title, from) {
  if (!dlg) build();
  // JC-EVO 09/28/2026 (D35): the poster carries the close label in the page language (locale key jc.video.close)
  closeBtn.setAttribute('aria-label', (from && from.dataset.jcCloseLabel) || 'Close video');
  dlg.setAttribute('aria-label', title || 'Video');
  stage.replaceChildren(media);                                                 // already open: just swap the player
  if (!active) {
    active = true;
    trigger = from || null;
    lockScroll(dlg);
    dlg.showModal();
  }
  closeBtn.focus();
  const v = stage.querySelector('video');
  if (v) v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });   // refused with sound -> muted (controls unmute)
}

function youtube(id, title) {
  const f = document.createElement('iframe');
  f.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1`;
  f.title = title || 'Video';
  f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  f.allowFullscreen = true;
  f.referrerPolicy = 'strict-origin-when-cross-origin';
  return f;
}

function fromTemplate(id, title) {
  const tpl = id && document.getElementById(id);
  const src = tpl && tpl.content && tpl.content.firstElementChild;
  if (!src) return null;
  const node = src.cloneNode(true);
  if (node.tagName === 'VIDEO') { node.controls = true; node.autoplay = true; node.playsInline = true; }
  else if (node.tagName === 'IFRAME' && !node.title) node.title = title || 'Video';
  return node;
}

function play(t) {
  const title = t.dataset.videoTitle || '';
  const media = t.dataset.jcLightbox === 'youtube'
    ? (t.dataset.videoId ? youtube(t.dataset.videoId, title) : null)
    : fromTemplate(t.dataset.template, title);
  if (media) open(media, title, t);
}

if (!window.JCVideoLightbox) {
  window.JCVideoLightbox = { open, close };
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-jc-lightbox]');
    if (!t) return;
    e.preventDefault();
    play(t);
  });
  // the film block's poster is a div[role=button]; real <button> posters get Enter/Space from the browser
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = e.target.closest('[data-jc-lightbox][role="button"]');
    if (!t || t !== e.target) return;
    e.preventDefault();
    play(t);
  });
}
